import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, Phone } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";

const Navbar = () => {
  const { lang, toggleLang } = useLanguage();
  const t = translations[lang].nav;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.home },
    { to: "/hizmetlerimiz", label: t.services },
    { to: "/hakkimizda", label: t.about },
    { to: "/blog", label: t.blog },
    { to: "/referanslar", label: t.references },
    { to: "/iletisim", label: t.contact },
  ];

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-navy-950/90 backdrop-blur-md border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="/assets/adalcrea-logo-white.png"
            alt="AdalCrea"
            className="h-11 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isActive(l.to)
                  ? "text-amber-400"
                  : "text-slate-200 hover:text-white hover:bg-white/5"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-200 hover:text-amber-400 transition-colors"
            aria-label="Change language"
          >
            <Globe className="w-4 h-4" />
            {lang === "tr" ? "EN" : "TR"}
          </button>
          <Link
            to="/iletisim"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-navy-950 font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors shadow-lg shadow-amber-500/20"
          >
            <Phone className="w-4 h-4" />
            {t.cta}
          </Link>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-navy-950/98 backdrop-blur-md border-t border-white/10 mt-3">
          <div className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-3 rounded-lg text-base font-medium ${
                  isActive(l.to)
                    ? "text-amber-400 bg-white/5"
                    : "text-slate-200 hover:bg-white/5"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/10">
              <button
                onClick={toggleLang}
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-200 px-3 py-2"
              >
                <Globe className="w-4 h-4" />
                {lang === "tr" ? "English" : "Türkçe"}
              </button>
              <Link
                to="/iletisim"
                className="flex-1 text-center bg-amber-500 text-navy-950 font-semibold text-sm px-4 py-3 rounded-lg"
              >
                {t.cta}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
